import { helpers, requiredIf } from '@vuelidate/validators';
// eslint-disable-next-line import/no-cycle
import { ActionABTestData } from '@/components/automated-scenarios/metadata/actions/ActionABTestMetadata';
import { i18n } from '@/i18n';
// eslint-disable-next-line import/no-cycle
import {
  voucherMethodsValues, voucherTypeValues,
} from '@/composables/automated-scenarios/AutomatedScenariosSendingChannel';
import { Moment } from 'moment';
// eslint-disable-next-line import/no-cycle
import getShopsDomains from '@/composables/shop/ShopsSendDomains';
// eslint-disable-next-line import/no-cycle
import { OperatorType } from '@/types/generated-types/graphql';
import { segmentationHasEmptyCondition, triggerSegmentationVisitError } from '@/composables/segmentations/segmentationValidation';

let translation: any;
(async () => {
  translation = await i18n;
})();

export function locationGMapsValidator(): any {
  return helpers.withMessage(translation.global.t('customValidator.locationGMaps'), (value: any) => {
    const formattedValue = '0' in value ? value[0] : value;
    return !((formattedValue.radius === undefined || formattedValue.radius === '')
      && (formattedValue.lat === undefined || Number.isNaN(formattedValue.lat))
      && (formattedValue.long === undefined || Number.isNaN(formattedValue.long))
      && (formattedValue.address === undefined || formattedValue.address === ''));
  });
}
export function itemsPickerValidator(required = true): any {
  return helpers.withMessage(translation.global.t('customValidator.itemsPicker'), (value: any) => (
    (!required && !value)
      || (value.selection && value.selection.selected && ((required && value.selection.selected.length > 0) || (!required && value.selection.selected.length >= 0)))
  ));
}

export function includesValuesValidator(data: any[]): any {
  return helpers.withMessage(translation.global.t('customValidator.includesValues'), (value: any) => data.includes(value));
}

export function requiredWithMessage(message?: string): any {
  const defaultMessage = translation.global.t('customValidator.required');
  return helpers.withMessage(message || defaultMessage, (value: any) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'object' && value !== null) {
      return Object.keys(value).length > 0;
    }
    return value !== null && value !== undefined && value !== '';
  });
}

/*
 * Blocks saving a contact-segmentation filter that still holds a blank condition.
 */
export function segmentationCompleteValidator(): any {
  return helpers.withMessage(
    translation.global.t('automatedScenarios.validation.segmentation.emptyCondition'),
    (value: any) => !segmentationHasEmptyCondition(value),
  );
}

/*
 * Blocks saving an on-site trigger segmentation whose « Visité des URLs »
 * conditions cannot be evaluated (visit duration outside the session…).
 */
export function triggerSegmentationVisitValidator(): any {
  return helpers.withMessage(
    ({ $model }: any) => translation.global.t(
      triggerSegmentationVisitError($model) ?? 'automatedScenarios.validation.segmentation.visitSessionCriterion',
    ),
    (value: any) => triggerSegmentationVisitError(value) === null,
  );
}

export function linkedBoxRequiredValidator(): any {
  return helpers.withMessage(
    translation.global.t('automatedScenarios.filters.linkedOperators.empty_error'),
    (value: string | string[]) => {
      // Handle both array (from form) and string (JSON from storage)
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      try {
        const parsed = JSON.parse(value || '[]');
        return Array.isArray(parsed) && parsed.length > 0;
      } catch {
        return false;
      }
    },
  );
}

/*
 * Validator used in the validation of the "Visit a page" trigger configuration form && "Navigation history" Filter configuration form
 */
export function minNavigationCriterionValidator(type: string, products: object, categories: object, manufacturers: object, urls: object): any {
  return helpers.withMessage(translation.global.t('customValidator.minNavigationCriterion'), (value: any) => {
    if (type === 'trigger' && value === 1) {
      return true;
    }
    return !!(Object.keys(products).length || Object.keys(categories).length || Object.keys(manufacturers).length
      || Object.keys(urls).length);
  });
}

/*
 * Validator used in the validation of the "AB Test" action configuration form
 */
export function ABTestDecreaseOutputsValidator(data: ActionABTestData): any {
  return helpers.withMessage(translation.global.t('customValidator.ABTestDecreaseOutputs'), (value: any) => {
    let validateBranchesToDelete = true;
    if (typeof data.initial_number_outputs !== 'undefined'
      && data.initial_number_outputs !== null
      && typeof data.outputs_to_delete !== 'undefined'
      && data.outputs_to_delete !== null
      && data.initial_number_outputs > data.outputs_number_test) {
      validateBranchesToDelete = (data.initial_number_outputs - data.outputs_number_test) === data.outputs_to_delete.length;
    }

    return validateBranchesToDelete;
  });
}

/*
 * Validator used in the validation of the filters with segments "Inclusion/Exclusion"
 */
export function atLeastOneSegmentValidator(dataExclusion: object): any {
  return helpers.withMessage(translation.global.t('customValidator.atLeastOneSegment'), requiredIf(() => !Object.keys(dataExclusion).length));
}

/*
 * Validator used in the validation of the filters with segments "Inclusion/Exclusion"
 */
export async function voucherTemplateValidator(data: any, field: string) {
  let required = false;
  const mustConfigVoucher = true;

  // if (data.template_display_selector_id && data.template_display_selector_id > 0) {
  //   const idTemplate = asInt(data.template_display_selector_id);
  //   let templatesContainsVoucher = false;
  //   const listTemplate = await getTemplateAndRedirect(idTemplate);
  //   if (listTemplate !== null) {
  //     templatesContainsVoucher = checkIfTemplateOrRedirectContainsVoucher(listTemplate);
  //   }
  //   if (templatesContainsVoucher && data.reuse_a_previous_coupon === reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER) {
  //     mustConfigVoucher = true;
  //   }
  // }

  if (mustConfigVoucher) {
    switch (field) {
      case 'method':
        required = true;
        break;
      case 'descript':
        required = false;
        break;
      case 'type':
        required = true;
        break;
      case 'identifiant':
        required = data.template_display_reduction_method === voucherMethodsValues.MANUAL;
        break;
      case 'amount':
        required = [voucherTypeValues.PERCENTAGE, voucherTypeValues.AMOUNT].includes(data.template_display_reduction_type);
        break;
      case 'duplicate':
        required = data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC && data.template_display_reduction_type === voucherTypeValues.DUPLICATED;
        break;
      case 'minorderamount':
        required = data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC;
        break;
      case 'validtime':
        required = data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC;
        break;
      default:
        required = false;
    }
  }
  return !required;
}

/*
  * Email format validator
 */
export function emailFormatValidator(): any {
  return helpers.withMessage(translation.global.t('customValidator.emailFormat'), (value: any) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(value);
  });
}

export function localizedTextInputValidator(type: string, mustVerify = true): any {
  if (!mustVerify) {
    return true;
  }

  // Utiliser un message spécifique selon le type
  let messageKey = 'customValidator.localizedTextInput';
  if (type === 'smsSenderName') {
    messageKey = 'customValidator.smsSenderName';
  } else if (type === 'tel') {
    messageKey = 'customValidator.phoneFormat';
  } else if (type === 'email') {
    messageKey = 'customValidator.emailFormat';
  }

  return helpers.withMessage(translation.global.t(messageKey), (value: any) => {
    // Determines if 'value' is a non-empty object with all it's properties are filled.
    const isFilled = typeof value === 'object' && value !== null && Object.keys(value).length > 0 && Object.values(value).every((val) => !!val);

    if (isFilled && (type === 'email' || type === 'url' || type === 'tel' || type === 'smsSenderName')) {
      const isValid = Object.values(value).every((val: any) => {
        if (type === 'email') {
          // Accept mailto:email@domain.com or email@domain.com
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const mailtoPattern = /^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return emailPattern.test(val) || mailtoPattern.test(val);
        }

        if (type === 'url') {
          return /^(ftp|http|https):\/\/[^ "]+$/.test(val);
        }

        if (type === 'tel') {
          // Accept tel:+33123456789 or raw phone number
          const telPattern = /^tel:[+\d\s()-]+$/;
          const phonePattern = /^[+\d\s()-]+$/;
          return telPattern.test(val) || phonePattern.test(val);
        }

        if (type === 'smsSenderName') {
          return val.length >= 3 && val.length <= 11 && !(new RegExp('^[0-9]+$')).test(val);
        }
        return false;
      });

      if (!isValid) {
        return false;
      }
    }

    return isFilled;
  });
}

export function dateBetweenValidator(dateMin: Moment | null, dateMax: Moment | null): any {
  let message = translation.global.t('customValidator.simpleDateTime.global');
  if (dateMin && dateMax) {
    message = translation.global.t('customValidator.simpleDateTime.between', { min: dateMin.format('DD/MM/YYYY HH:mm:ss'), max: dateMax.format('DD/MM/YYYY HH:mm:ss') });
  } else if (dateMin) {
    message = translation.global.t('customValidator.simpleDateTime.min', { min: dateMin.format('DD/MM/YYYY HH:mm:ss') });
  } else if (dateMax) {
    message = translation.global.t('customValidator.simpleDateTime.max', { max: dateMax.format('DD/MM/YYYY HH:mm:ss') });
  }

  return helpers.withMessage(message, (value: Moment) => {
    if (typeof value !== 'object') {
      return true;
    }

    if (dateMin && dateMax) {
      return value.isBetween(dateMin, dateMax);
    }
    if (dateMin) {
      return value.isAfter(dateMin);
    }
    if (dateMax) {
      return value.isBefore(dateMax);
    }
    return true;
  });
}

export function verifyCommercialUseCampaign(campaignContainMediaWithVoucher: boolean): any {
  return helpers.withMessage(translation.global.t('customValidator.campaignCommercialUse'), (value: any) => !(value === 0 && campaignContainMediaWithVoucher));
}

export function urlFormatValidator(): any {
  return helpers.withMessage(translation.global.t('customValidator.urlFormat'), (value: any) => /^(ftp|http|https):\/\/[^ "]+$/.test(value));
}

export function domaineNameInputValidator(): any {
  return helpers.withMessage(translation.global.t('shop.my_domains.add.inValid'), (value: any) => {
    if (!value.trim()) {
      return false;
    }

    const domainRegex = /^(?!:\/\/)(?!www\.|m\.)[a-zA-Z0-9-]{1,63}(\.[a-zA-Z]{2,})+$/;
    return domainRegex.test(value);
  });
}

export function voucherInputValidator(): any {
  return helpers.withMessage(translation.global.t('storeParameters.services.voucherServiceParameter.invalidFieldValue'), (value: any) => {
    if (!value) {
      return false;
    }

    return value > 0;
  });
}

export function importedHtmlValidator(): any {
  return helpers.withMessage(translation.global.t('templateSelector.import.noUnsubscribeUrlFound'), (value: string) => {
    const mandatoryVariableRegex = /\{var=template\.unsubscribe_url\}/gm;
    return mandatoryVariableRegex.test(value);
  });
}

export function folderNameValidator(): any {
  return helpers.withMessage(translation.global.t('fileManager.errors.createFolderNoSpace'), (value: any) => {
    if (value === undefined || value === null || value.trim() === '') {
      return false;
    }
    const re = /\s/;
    return !re.test(value.trim());
  });
}

export function validateColumnProportions(value: string): boolean {
  if (value === undefined || value === null || value.trim() === '') {
    return false;
  }
  const percentages = value
    .split('/')
    .map((part) => parseInt(part.trim().replace('%', ''), 10));

  if (percentages.some(Number.isNaN)) {
    return false;
  }

  if (percentages.length === 3 && percentages.every((num) => num === 33)) {
    return true;
  }

  const total = percentages.reduce((sum, num) => sum + num, 0);
  return total === 100;
}

export function columnProportionsValidator() {
  return helpers.withMessage(
    translation.global.t('templateBuilder.modals.columnWidget.validation.errors.totalProportionsMustEqual100'),
    (value: string) => validateColumnProportions(value),
  );
}

export async function validateSenderDomain() {
  const senderDomains = await getShopsDomains(0, [{
    field: 'domain_verified',
    operator: OperatorType.Equals,
    value: true,
  }]);

  const allowedDomains = senderDomains.map((d) => d.domain.toLowerCase());

  return helpers.withMessage(translation.global.t('customValidator.senderDomain'), helpers.withAsync(async (value: any) => {
    // Determines if 'value' is a non-empty object and all it's properties are filled, or an array with values, or a string with a value.
    const isFilled = (typeof value === 'string' && value.trim() !== '')
      || (Array.isArray(value) && value.every((v) => typeof v === 'string' && v.trim() !== ''))
      || (typeof value === 'object' && value !== null && !Array.isArray(value)
        && Object.values(value).every((v) => typeof v === 'string' && v.trim() !== ''));

    if (isFilled) {
      // Get strings from value (string, array, object)
      const extractStrings = (input: any): string[] => {
        if (typeof input === 'string') return [input];
        if (Array.isArray(input)) return input.filter((v): v is string => typeof v === 'string');
        if (typeof input === 'object' && input !== null) {
          return Object.values(input).filter((v): v is string => typeof v === 'string');
        }
        return [];
      };

      // Get emails from value
      const emails = extractStrings(value).map((v) => v.trim()).filter((v) => v !== '');

      // Check if email matches one of the validated domains
      return emails.every((email) => {
        const match = email.match(/^[^@]+@([^@]+\.[^@]+)$/);
        if (!match) return false;

        const domain = match[1].toLowerCase();
        return allowedDomains.includes(domain);
      });
    }

    return isFilled;
  }));
}
